import 'swiper/css/bundle';
import Swiper from 'swiper/bundle';

import './hero-slider.scss';

const heroSlider = () => {
	const sliders = document.querySelector('.hero-homepage-swiper .swiper');
	if (!sliders) return;

	const parent 		= sliders.closest('.hero-homepage-swiper');
	const navButtons 	= parent.dataset.navButtons === "true";
	const pagination 	= parent.dataset.pagination === "true";
	const autoplay 		= parent.dataset.autoplay === "true";
	const speed 		= parseInt(parent.dataset.speed) || 5000;
	const speedChanges 	= parseInt(parent.dataset.speedChanges) || 600;
	const loop 			= parent.dataset.loop  === "true";

	const players = new WeakMap();

	let ytApiReady = null;

	function loadYouTubeAPI() {
		if (ytApiReady) return ytApiReady;

		ytApiReady = new Promise((resolve) => {
			if (window.YT && window.YT.Player) {
				resolve(window.YT);
				return;
			}
			const tag = document.createElement('script');
			tag.src   = 'https://www.youtube.com/iframe_api';
			document.head.appendChild(tag);

			window.onYouTubeIframeAPIReady = () => resolve(window.YT);
		});

		return ytApiReady;
	}

	let vimeoApiReady = null;

	function loadVimeoAPI() {
		if (vimeoApiReady) return vimeoApiReady;

		vimeoApiReady = new Promise((resolve) => {
			if (window.Vimeo && window.Vimeo.Player) {
				resolve(window.Vimeo.Player);
				return;
			}
			const tag   = document.createElement('script');
			tag.src     = 'https://player.vimeo.com/api/player.js';
			tag.onload  = () => resolve(window.Vimeo.Player);
			document.head.appendChild(tag);
		});

		return vimeoApiReady;
	}

	function getYouTubeId(src) {
		const m = src.match(/embed\/([a-zA-Z0-9_-]{11})/);
		return m ? m[1] : '';
	}

	function getVimeoId(src) {
		const m = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
		return m ? m[1] : '';
	}

	let heroSwiper = null;
	let isAdvancing = false;

	function goNext() {
		if (!autoplay) return;
		if (!heroSwiper || isAdvancing) return;
		isAdvancing = true;
		setTimeout(() => { isAdvancing = false; }, 1000);

		if (loop || heroSwiper.activeIndex < heroSwiper.slides.length - 1) {
			heroSwiper.slideNext();
		}
	}

	async function initYouTube(slide) {
		const src    = slide.dataset.videoSrc;
		const id     = getYouTubeId(src);
		const poster = slide.querySelector('.video-poster');
		const wrapper = slide.querySelector('.video-wrapper');
		if (!id || !wrapper) return;

		const YT     = await loadYouTubeAPI();
		const div = document.createElement('div');
		wrapper.appendChild(div);

		return new Promise((resolve) => {
			const player = new YT.Player(div, {
				videoId    : id,
				playerVars : {
					autoplay       : 1,
					mute           : 1,
					controls       : 0,
					disablekb      : 1,
					modestbranding : 1,
					rel            : 0,
					playsinline    : 1,
					loop           : 0,
					// playlist       : id,
				},
				events: {
					onReady(e) {
						e.target.playVideo();
						poster?.classList.add('is-hidden');
						players.set(slide, { type: 'youtube', player });
						resolve();
					},
					onStateChange(e) {
						if (e.data === 0) {
							goNext();
						}
					},
					onError(e) {
						console.warn('YouTube player error:', e.data);
					}
				}
			});
		});
	}

	async function initVimeo(slide) {
		const src    = slide.dataset.videoSrc;
		const id     = getVimeoId(src);
		const poster = slide.querySelector('.video-poster');
		const wrapper     = slide.querySelector('.video-wrapper');
		if (!id|| !wrapper) return;

		const VimeoPlayer = await loadVimeoAPI();

		const player = new VimeoPlayer(wrapper, {
			id         : parseInt(id),
			autoplay   : true,
			muted      : true,
			controls   : false,
			loop       : false,
			background : false,
			autopause  : false,
			responsive : false,
		});

		player.ready().then(() => {
			player.play().catch(() => {});
			poster?.classList.add('is-hidden');
			players.set(slide, { type: 'vimeo', player });

			player.on('ended', () => {
				goNext();
			});
		});
	}

	function initTagVideos() {
		const slides = sliders.querySelectorAll('.swiper-slide');

		slides.forEach((slide) => {
			const video = slide.querySelector('video');
			if (!video) return;

			video.removeAttribute('loop');
			video.loop = false;

			video.removeAttribute('controls');
			video.controls = false;

			video.muted = true;

			video.addEventListener('ended', () => {
				goNext();
			});
		});
	}

	async function loadVideo(slide, shouldAutoplay = false) {
		if (!slide.dataset.videoSrc) return;

		const src    = slide.dataset.videoSrc;
		const poster = slide.querySelector('.video-poster');

		if (players.has(slide)) {
			const { type, player } = players.get(slide);
			if (shouldAutoplay) {
				if (type === 'youtube') {
					player.seekTo(0);
					player.playVideo();
					poster?.classList.add('is-hidden');
				}
				if (type === 'vimeo') {
					player.setCurrentTime(0).then(() => {
						player.play().catch(() => {});
					});
					poster?.classList.add('is-hidden');
				}
			}
			return;
		}

		if (src.includes('youtube.com')) {
			await initYouTube(slide);
		} else if (src.includes('vimeo.com')) {
			await initVimeo(slide);
		}
	}

	function pauseVideo(slide) {
		if (!players.has(slide)) return;
		const { type, player } = players.get(slide);

		if (type === 'youtube') player.pauseVideo();
		if (type === 'vimeo')   player.pause().catch(() => {});
		const video = slide.querySelector('video');
		if (video) video.pause();
	}

	function playVideo(slide) {
		const video = slide.querySelector('video');
		if (video) {
			video.currentTime = 0;
			video.play().catch(() => {});
		}
	}

	function pauseInactiveVideos(swiper) {
		swiper.slides.forEach((slide, i) => {
			if (i !== swiper.activeIndex) pauseVideo(slide);
		});
	}

	// function pauseAllTagVideos(videos) {
	// 	Array.prototype.forEach.call(videos, video => video.pause());
	// }

	// function playActiveTagVideo(sliders, activeIndex) {
	// 	const activeSlide = sliders.getElementsByClassName('swiper-slide')[activeIndex];
	// 	const activeVideo = activeSlide?.getElementsByTagName('video')[0];
	// 	activeVideo?.play();
	// }

	const args = {
        navigation: navButtons ? {
			nextEl: ".swiper-button-next",
			prevEl: ".swiper-button-prev",
		} : false,
		pagination: pagination ? {
			el: ".swiper-pagination",
			clickable: true,
		} : false,
		autoplay: autoplay ? {
            delay: speed,
            disableOnInteraction: false,
        } : false,
        speed: speedChanges,
		loop: loop,
		on: {
			afterInit(swiper) {
				initTagVideos();

				const active = swiper.slides[swiper.activeIndex];
				if (!active) return;
				const hasVideo = active.dataset.videoSrc || active.querySelector('video');
				if (hasVideo && autoplay) {
					swiper.autoplay.stop();
				}
				loadVideo(active, true);
				playVideo(active);
			},
			slideChangeTransitionEnd(swiper) {
				pauseInactiveVideos(swiper);

				const active = swiper.slides[swiper.activeIndex];
				if (!active) return;
				const hasVideo = active.dataset.videoSrc || active.querySelector('video');

				if (hasVideo) {
					if (autoplay) swiper.autoplay.stop();
				} else {
					if (autoplay) swiper.autoplay.start();
				}

				loadVideo(active, true);

				playVideo(active);
			},
		}

    };

    heroSwiper = new Swiper(sliders, args);
};

export default heroSlider;
